/* eslint-disable */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus, FileSpreadsheet, FileText, CheckCircle2 } from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function NewCustomersAlert({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [checked, setChecked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/new-customers`);
      setItems(r.data || []);
      setChecked(new Set());
    } catch { /* ruolo non abilitato o errore: sezione nascosta */ }
  };
  useEffect(() => { load(); }, [refreshKey]);

  if (items.length === 0) return null;

  const toggle = (id) => setChecked((prev) => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });
  const allChecked = items.length > 0 && items.every((c) => checked.has(c.id));
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(items.map((c) => c.id)));
  const targetIds = checked.size > 0 ? [...checked] : items.map((c) => c.id);

  const exportFile = async (fmt) => {
    try {
      const r = await axios.get(`${API}/new-customers/export?fmt=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nuovi-clienti-${new Date().toISOString().slice(0, 10)}.${fmt === "csv" ? "csv" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore esportazione");
    }
  };

  const markDone = async () => {
    const n = targetIds.length;
    if (!window.confirm(`Segnare ${n} client${n === 1 ? "e" : "i"} come inserit${n === 1 ? "o" : "i"} nei gestionali esterni?\nSpariranno da questa lista.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/new-customers/mark-synced`, { customer_ids: targetIds });
      toast.success(`${r.data.marked} client${r.data.marked === 1 ? "e segnato" : "i segnati"} come inserit${r.data.marked === 1 ? "o" : "i"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-6 border-2 border-amber-400 bg-amber-50/60 overflow-hidden" data-testid="new-customers-alert">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-amber-400 px-4 py-2.5">
        <h3 className="font-extrabold text-stone-900 text-sm uppercase tracking-wide flex items-center gap-2">
          <UserPlus className="w-4 h-4" /> Nuovi clienti da inserire nei gestionali esterni
          <span className="bg-stone-900 text-white text-xs font-bold px-2.5 py-0.5 rounded-full" data-testid="new-customers-count">{items.length}</span>
        </h3>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => exportFile("xlsx")} className="h-8 bg-white border-stone-300 text-stone-800" data-testid="new-customers-export-xlsx">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportFile("csv")} className="h-8 bg-white border-stone-300 text-stone-800" data-testid="new-customers-export-csv">
            <FileText className="w-4 h-4 mr-1" /> CSV
          </Button>
          <Button size="sm" onClick={markDone} disabled={busy} className="h-8 bg-stone-900 hover:bg-black text-white" data-testid="new-customers-mark-synced">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Segna come inseriti ({targetIds.length})
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-amber-100/70 text-stone-700 border-b border-amber-300">
            <tr>
              <th className="p-2 w-8 text-center">
                <input type="checkbox" checked={allChecked} onChange={toggleAll} className="w-4 h-4 accent-stone-900 cursor-pointer" data-testid="new-customers-select-all" />
              </th>
              <th className="p-2 text-left">Codice</th>
              <th className="p-2 text-left">Ragione sociale</th>
              <th className="p-2 text-left">P.IVA</th>
              <th className="p-2 text-left">Città</th>
              <th className="p-2 text-left">Agente</th>
              <th className="p-2 text-left">Creato da</th>
              <th className="p-2 text-left">Data</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amber-200/60">
            {items.map((c) => (
              <tr key={c.id} className="hover:bg-amber-100/40" data-testid={`new-customer-row-${c.codice_cliente}`}>
                <td className="p-2 text-center">
                  <input type="checkbox" checked={checked.has(c.id)} onChange={() => toggle(c.id)} className="w-4 h-4 accent-stone-900 cursor-pointer" data-testid={`new-customer-check-${c.codice_cliente}`} />
                </td>
                <td className="p-2 font-mono text-xs">{c.codice_cliente}</td>
                <td className="p-2 font-medium text-stone-900">{c.business_name}</td>
                <td className="p-2 text-stone-600">{c.vat_number || "—"}</td>
                <td className="p-2 text-stone-600">{c.city || "—"}</td>
                <td className="p-2 text-stone-600">{c.agent_name || "—"}</td>
                <td className="p-2 text-stone-600">{c.created_by_name || "—"} {c.created_by_role ? <span className="text-[10px] text-stone-400">({c.created_by_role})</span> : null}</td>
                <td className="p-2 text-stone-500 text-xs">{(c.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
